import { FaceValidationMeasurement } from '@/api/v2/faceValidation';

/**
 * #1870. The thresholds that decide whether an uploaded photo passes.
 *
 * Defaults mirror `checkout.facevalidation.*` — keep them in step, they are what the calibration
 * screen starts from and what the numbers below are compared against.
 */
export type Thresholds = {
    occlusionConfidence: number;
    sunglassesConfidence: number;
    minSharpness: number;
    minBrightness: number;
    ageToleranceYears: number;
};

export const DEFAULT_THRESHOLDS: Thresholds = {
    occlusionConfidence: 80,
    sunglassesConfidence: 80,
    minSharpness: 30,
    minBrightness: 20,
    ageToleranceYears: 8,
};

export type Verdict =
    | 'APPROVED'
    | 'NO_FACE_DETECTED'
    | 'FACE_OCCLUDED'
    | 'LOW_IMAGE_QUALITY';

/**
 * Replays the check for one measurement under a given set of thresholds.
 *
 * This mirrors `FaceValidationService.evaluate` in the checkout service. It is deliberately a second
 * implementation rather than a call to the backend: moving a slider has to redraw the whole set
 * instantly, and a round trip per measurement per keystroke would make the screen unusable. The
 * rules are five threshold comparisons, so the duplication is small — but it is duplication, and
 * both sides have to be changed together. The backoffice has no test setup to pin that with, so the
 * authoritative version stays `FaceValidationServiceTest` in the checkout service: change the rules
 * there first, then mirror them here.
 */
export const evaluate = (
    m: FaceValidationMeasurement,
    t: Thresholds
): Verdict => {
    if (m.faceCount < 1) return 'NO_FACE_DETECTED';

    if (
        m.occluded === true &&
        (m.occludedConfidence ?? 0) >= t.occlusionConfidence
    )
        return 'FACE_OCCLUDED';

    if (
        m.sunglasses === true &&
        (m.sunglassesConfidence ?? 0) >= t.sunglassesConfidence
    )
        return 'FACE_OCCLUDED';

    if (
        (m.sharpness ?? Infinity) <= t.minSharpness ||
        (m.brightness ?? Infinity) <= t.minBrightness
    )
        return 'LOW_IMAGE_QUALITY';

    return 'APPROVED';
};

/** The age check never rejects — it is a hint, because age estimates are too coarse to block on. */
export const hasAgeWarning = (
    m: FaceValidationMeasurement,
    t: Thresholds,
    today = new Date()
): boolean => {
    if (m.birthYear === null || m.ageLow === null || m.ageHigh === null)
        return false;
    const age = today.getFullYear() - m.birthYear;
    return (
        age < m.ageLow - t.ageToleranceYears ||
        age > m.ageHigh + t.ageToleranceYears
    );
};

export type Summary = {
    total: number;
    approved: number;
    rejected: number;
    byReason: Record<string, number>;
    ageWarnings: number;
    /** Share of photos that would be rejected. Not the error rate — see falseRejectionRate. */
    rejectionRate: number;
    /** How many photos a person has judged so far. */
    labelled: number;
    /** Judged good but would be rejected — these are the ones that hurt. */
    falseRejections: number;
    /** Judged bad but would pass — the check missing something. */
    falseAcceptances: number;
    /**
     * The number the go-live gate is actually written against: of the photos a person called fine,
     * how many would be rejected anyway. Null until something has been judged — a rate over zero
     * judged photos is not a rate.
     */
    falseRejectionRate: number | null;
};

export const summarise = (
    measurements: FaceValidationMeasurement[],
    t: Thresholds
): Summary => {
    const byReason: Record<string, number> = {};
    let approved = 0;
    let ageWarnings = 0;
    let labelled = 0;
    let labelledGood = 0;
    let falseRejections = 0;
    let falseAcceptances = 0;

    measurements.forEach((m) => {
        const verdict = evaluate(m, t);
        const passes = verdict === 'APPROVED';
        if (passes) approved += 1;
        else byReason[verdict] = (byReason[verdict] ?? 0) + 1;
        if (hasAgeWarning(m, t)) ageWarnings += 1;

        if (m.humanLabel) {
            labelled += 1;
            if (m.humanLabel === 'GOOD') {
                labelledGood += 1;
                if (!passes) falseRejections += 1;
            } else if (passes) {
                falseAcceptances += 1;
            }
        }
    });

    const total = measurements.length;
    const rejected = total - approved;

    return {
        total,
        approved,
        rejected,
        byReason,
        ageWarnings,
        rejectionRate: total === 0 ? 0 : (rejected / total) * 100,
        labelled,
        falseRejections,
        falseAcceptances,
        falseRejectionRate:
            labelledGood === 0 ? null : (falseRejections / labelledGood) * 100,
    };
};
